import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { downloadZwo } from '../services/xmlParser';
import { generateFitWorkout, getNormalizedFitDownloadFilename } from '../services/fitGenerator';
import type { TranslationKeys } from '@/core/i18n/translations';
import type { SportType, WorkoutSegment } from '@/types';
import { shareZwoFile, shareFitBlob, triggerFitDownload } from '../utils/nativeShare';

/**
 * ZWO/FIT download flows with Web Share API fallback, clipboard copy for markdown, and plain-text share.
 */
export function useWorkoutFileExport(
  generatedXml: string,
  generatedMarkdown: string,
  workoutName: string,
  language: 'en' | 'pl',
  t: TranslationKeys,
  isPro: boolean,
  setShowPricingModal: React.Dispatch<React.SetStateAction<boolean>>,
  segments: WorkoutSegment[],
  sport: SportType,
  storedFtp: number | null,
) {
  const [copied, setCopied] = useState(false);

  const handleDownloadZwo = useCallback(async () => {
    if (!isPro) {
      setShowPricingModal(true);
      toast.error(t.proFeatureRequired);
      return;
    }
    if (!generatedXml) {
      toast.error(t.errorNoZwoContent);
      return;
    }

    const filename = `${workoutName}_${language}.zwo`;
    const shared = await shareZwoFile(generatedXml, filename, workoutName);
    if (!shared) {
      downloadZwo(generatedXml, filename);
    }
  }, [generatedXml, workoutName, language, isPro, setShowPricingModal, t]);

  const handleDownloadFit = useCallback(
    async (resolvedFtp?: number | null) => {
      if (!segments.length) {
        toast.error(t.errorNoZwoContent);
        return;
      }
      if (sport !== 'bike') {
        toast.error(t.fitBikeOnlyToast);
        return;
      }

      const ftpRaw =
        typeof resolvedFtp === 'number' && Number.isFinite(resolvedFtp) && resolvedFtp >= 30
          ? resolvedFtp
          : typeof storedFtp === 'number' && Number.isFinite(storedFtp) && storedFtp >= 30
            ? storedFtp
            : null;
      if (ftpRaw == null) {
        toast.error(t.fitFtpExportBlocked);
        return;
      }
      const ftp = Math.round(ftpRaw);

      const blob = generateFitWorkout({ workoutName, segments, sport }, ftp);
      const filename = getNormalizedFitDownloadFilename(workoutName);

      const shared = await shareFitBlob(blob, filename, workoutName);
      if (!shared) {
        triggerFitDownload(blob, filename);
      }
    },
    [segments, sport, workoutName, storedFtp, t],
  );

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(generatedMarkdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [generatedMarkdown]);

  const handleShareWorkoutDescription = useCallback(async () => {
    const text = generatedMarkdown.trim();
    if (!text) {
      toast.error(t.errorNoZwoContent);
      return;
    }
    if (!navigator.share) {
      await navigator.clipboard.writeText(text);
      toast.success(t.copied);
      return;
    }
    try {
      await navigator.share({
        title: workoutName || t.shareWorkoutDescriptionBtn,
        text,
      });
    } catch (err) {
      const name = err instanceof DOMException ? err.name : (err as Error)?.name;
      if (name === 'AbortError') return;
      console.error('[useWorkoutFileExport] share description failed:', err);
      toast.error(t.error);
    }
  }, [generatedMarkdown, workoutName, t]);

  return {
    copied,
    handleDownloadZwo,
    handleDownloadFit,
    handleShareWorkoutDescription,
    copyToClipboard,
  };
}
